import { Inject, Injectable } from '@angular/core';
import { AppSettings, AssetUIModel, ClientEnvironment, ENVIRONMENT } from '@thehubz/client/core';
import { AnyType } from '@thehubz/shared/model';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AssetsBulkEditApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { lastValueFrom } from 'rxjs';
import { AssetEditPopupComponent } from '../../asset-edit-popup/asset-edit-popup.component';
import { isUsageRightsEdit } from './../../assets/assets-page.util';
import { AssetsPageService } from './../../assets/assets-page/assets-page.service';

@Injectable({
  providedIn: 'root',
})
export class CollectionAssetCommonService {
  dialogEditState?: number;
  dialogEditRef!: MatDialogRef<AssetEditPopupComponent>;
  readonly brandingKey: CustomClients;

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    private dialog: MatDialog,
    private assetsPageService: AssetsPageService,
    private bulkEditApiService: AssetsBulkEditApiService,
  ) {
    this.brandingKey = this.env.brandingKey;
  }

  /**
   * Update the asset session storage with the updated assets
   * @param updatedAssets
   */
  updateAssetSessionStorage(updatedAssets: AssetUIModel[]) {
    // Parse the selected items from the session storage
    const selectedItems = JSON.parse(sessionStorage[AppSettings.SESSION_STORAGE_KEY]);
    // Find the index of the 'assets' type in the selected items
    const assetTypeIndex = selectedItems.findIndex((obj: { type: string }) => obj.type === 'collection');
    const selectedAssetItems = selectedItems[assetTypeIndex];
    if (selectedAssetItems?.items?.length > 0 && updatedAssets.length > 0) {
      const newItems: AssetUIModel[] = [];
      selectedAssetItems.items.forEach((item: AssetUIModel) => {
        const updateIndex = updatedAssets.findIndex((asset: AssetUIModel) => asset._id === item._id);
        if (updateIndex != -1) {
          newItems.push(updatedAssets[updateIndex]);
        }
      });
      selectedAssetItems.items = [...newItems];
    }
    sessionStorage.setItem(AppSettings.SESSION_STORAGE_KEY, JSON.stringify(selectedItems));
  }

  /**
   * Opens an edit dialog for the other edits (metadata & relations) of assets
   * @param event
   */
  private async openEditDialog(event: { editType: string; assets: AssetUIModel[]; domainMatching?: boolean }) {
    let getProductionDetailsEditModel: AnyType;

    if (event.editType === 'metaData' && this.brandingKey === CustomClients.Standard) {
      getProductionDetailsEditModel = await lastValueFrom(
        this.bulkEditApiService.assetsBulkEditControllerGetProductionDetailsEditModel({ ids: event.assets.map((asset) => asset._id) }),
      );
      getProductionDetailsEditModel = !getProductionDetailsEditModel.assetsWithoutProductionDetails ? getProductionDetailsEditModel : undefined;
    }

    const dialogEditRef = this.dialog.open(AssetEditPopupComponent, {
      maxWidth: '120rem',
      width: '90%',
      height: '90%',
      disableClose: true,
      data: { ...event, productionDetails: getProductionDetailsEditModel },
    });
    this.dialogEditState = dialogEditRef.getState();
    dialogEditRef.afterClosed().subscribe(async (result: { count: number; success: string; updatedAssets: AssetUIModel[] }) => {
      this.dialogEditState = undefined;
      if (result && result.updatedAssets?.length > 0) {
        this.updateAssetSessionStorage(result.updatedAssets);
      }
    });
  }

  editAssets(event: { editType: string; assets: AssetUIModel[]; domainMatching?: boolean }) {
    this.assetsPageService.editDialogConfirmation(event.assets, event.editType).subscribe((data: AnyType) => {
      if (data) {
        // Check if the dialog edit state is invalid
        if (this.assetsPageService.isDialogEditStateInvalid(this.dialogEditState)) {
          const { editType, assets } = event;
          /* It will avoid global assets object editing. Actually we are removing the usage right from assets as per the
           * implementation, that is why it not showing the buyout rights when we edit the asset again.
           * we are creating a deep copy to avoid original assets object editing*/
          const newAssets = JSON.parse(JSON.stringify(assets));
          event.domainMatching = data?.domainMatching && data?.domainMatching === 'false' ? false : true;
          // Check if the edit type is 'usageRights' and the branding key is supported
          if (isUsageRightsEdit(editType) && this.assetsPageService.isSupportedBranding()) {
            // Handle the usage rights edit for the provided assets
            this.assetsPageService.handleUsageRightsEdit(newAssets);
          } else {
            event.assets = newAssets;
            // Open the edit dialog with the provided event (metadata & relations)
            this.openEditDialog(event);
          }
        }
      }
    });
  }
}
